// Polyfill for requestIdleCallback for browsers that don't support it
if (!window.requestIdleCallback) {
	window.requestIdleCallback = function (callback, _options) {
		const start = Date.now();
		return setTimeout(function () {
			callback({
				didTimeout: false,
				timeRemaining: function () {
					return Math.max(0, 50 - (Date.now() - start));
				}
			});
		}, 1);
	};
}

// Lazy load SVG sprite only if SVG icons are used on the page
async function loadSVGSprite() {
	const svgElements = document.querySelectorAll('svg use[href^="#icon-sprite-"]');
	if (svgElements.length > 0) {
		await import('virtual:svg-icons-register');
	}
}

// Lazy load Preline only if needed
async function loadPreline() {
	const prelineElements = document.querySelectorAll('[data-hs-overlay], [data-hs-collapse], [data-hs-dropdown], [data-hs-tab], [data-hs-accordion], [data-hs-scrollspy]');
	if (prelineElements.length > 0) {
		try {
			await import('preline');
			// Manually trigger Preline initialization after dynamic import
			if (window.HSStaticMethods && typeof window.HSStaticMethods.autoInit === 'function') {
				window.HSStaticMethods.autoInit();
			}
		} catch (error) {
			// Error loading Preline
		}
	}
}

// Initialize sliders with IntersectionObserver for performance
async function initSliders() {
	const sliderElements = document.querySelectorAll('[data-slidername], .carousel.slide');
	if (sliderElements.length === 0) return;

	// const sliderConfig = {
	// 	carouselNavClasses: {
	// 		default: {
	// 			navClasses: 'size-8 max-h-8 max-w-8 flex items-center justify-center bg-dark-500 text-white border-0',
	// 			prevClasses: 'left-4 -scale-x-100',
	// 			nextClasses: 'right-4',
	// 			navIconClasses: 'hidden',
	// 			prevIconClasses: '',
	// 			nextIconClasses: '',
	// 		},
	// 	}
	// };

	let slidersLoaded = false;

	// Function to load sliders
	const loadSliders = () => {
		if (slidersLoaded) return;
		slidersLoaded = true;

		import('./sliders.js')
		// .then(({ themeSliders }) => {
		// 	// new themeSliders(sliderConfig);
		// })
		// .catch(() => {
		// 	slidersLoaded = false; // Reset on error to allow retry
		// 	// Error loading sliders.js
		// });
	};

	// Check if any slider is already in viewport on page load
	const isInViewport = (element) => {
		const rect = element.getBoundingClientRect();
		const windowHeight = window.innerHeight || document.documentElement.clientHeight;
		const windowWidth = window.innerWidth || document.documentElement.clientWidth;

		// Check if ANY part of element is visible in viewport (with 50px margin)
		const verticalVisible = rect.top < windowHeight + 50 && rect.bottom > -50;
		const horizontalVisible = rect.left < windowWidth + 50 && rect.right > -50;

		return verticalVisible && horizontalVisible;
	};

	// Check if any slider is already visible
	let hasVisibleSlider = false;
	sliderElements.forEach(slider => {
		if (isInViewport(slider)) {
			hasVisibleSlider = true;
		}
	});

	// If any slider is already visible, load immediately
	if (hasVisibleSlider) {
		loadSliders();
		return; // Exit early, no need for observer
	}

	// Otherwise, use IntersectionObserver for lazy loading
	const observer = new IntersectionObserver((entries) => {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				loadSliders();
				observer.disconnect(); // Load once and disconnect all observations
			}
		});
	}, {
		rootMargin: '50px' // Start loading 50px before entering viewport
	});

	// Observe ALL slider elements
	sliderElements.forEach(slider => {
		observer.observe(slider);
	});
}

// Initialize galleries with IntersectionObserver for performance
async function initGalleries() {
	const galleryElements = document.querySelectorAll('.block--gallery');
	if (galleryElements.length === 0) return;

	let galleriesLoaded = false;

	// Function to load galleries
	const loadGalleries = () => {
		if (galleriesLoaded) return;
		galleriesLoaded = true;

		import('./galleries.js')
			.then(({ themeGlightbox }) => {
				new themeGlightbox();
			})
			.catch(() => {
				galleriesLoaded = false; // Reset on error to allow retry
				// Error loading galleries.js
			});
	};

	// Check if any gallery is already in viewport on page load
	const isInViewport = (element) => {
		const rect = element.getBoundingClientRect();
		const windowHeight = window.innerHeight || document.documentElement.clientHeight;
		const windowWidth = window.innerWidth || document.documentElement.clientWidth;

		// Check if ANY part of element is visible in viewport (with 50px margin)
		const verticalVisible = rect.top < windowHeight + 50 && rect.bottom > -50;
		const horizontalVisible = rect.left < windowWidth + 50 && rect.right > -50;

		return verticalVisible && horizontalVisible;
	};

	// Check if any gallery is already visible
	let hasVisibleGallery = false;
	galleryElements.forEach(gallery => {
		if (isInViewport(gallery)) {
			hasVisibleGallery = true;
		}
	});

	// If any gallery is already visible, load immediately
	if (hasVisibleGallery) {
		loadGalleries();
		return; // Exit early, no need for observer
	}

	// Otherwise, use IntersectionObserver for lazy loading
	const observer = new IntersectionObserver((entries) => {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				loadGalleries();
				observer.disconnect(); // Load once and disconnect all observations
			}
		});
	}, {
		rootMargin: '50px' // Start loading 50px before entering viewport
	});

	// Observe ALL gallery elements
	galleryElements.forEach(gallery => {
		observer.observe(gallery);
	});
}

// Initialize show more toggles
function initShowMoreToggles() {
	const showMoreToggles = document.querySelectorAll('.showmoretoggle');
	if (showMoreToggles.length === 0) return;

	showMoreToggles.forEach((toggle) => {
		// Initialize the toggle state - ensure "showless" text is hidden by default
		const showLessText = toggle.querySelector('.showless');
		if (showLessText) {
			showLessText.style.display = 'none';
		}

		// Initialize the target element to collapsed state
		const targetId = toggle.getAttribute('data-target');
		const targetElement = document.getElementById(targetId);
		if (targetElement) {
			// Determine if this is a company description or gallery
			const isCompanyDescription = targetId.includes('Description');

			// Remove conflicting CSS classes and set initial collapsed state
			targetElement.classList.remove('lg:max-h-[320px]', 'is-active');

			// Set appropriate initial height based on content type
			if (isCompanyDescription) {
				targetElement.style.maxHeight = '200px'; // Shorter for descriptions
			} else {
				targetElement.style.maxHeight = '320px'; // Standard for galleries
			}
			targetElement.style.overflow = 'hidden';
		}

		toggle.addEventListener('click', function () {
			const clickedTargetId = this.getAttribute('data-target');
			const parentTargetId = this.getAttribute('data-parent-target');

			// Store the current scroll position before making changes
			const scrollPosition = window.scrollY;

			const clickedTargetElement =
				document.getElementById(clickedTargetId);
			const parentElement = document.getElementById(parentTargetId);

			if (clickedTargetElement) {
				// Determine if this is a company description or gallery
				const isCompanyDescription =
					clickedTargetId.includes('Description');

				// Remove conflicting Tailwind classes so inline styles can take control
				clickedTargetElement.classList.remove('lg:max-h-[320px]');

				clickedTargetElement.classList.toggle('is-active');

				// Toggle max-height for smooth animation
				if (clickedTargetElement.classList.contains('is-active')) {
					// Expand
					clickedTargetElement.style.maxHeight = 'none';
					clickedTargetElement.style.overflow = 'visible';

					// Remove gradient classes when expanded
					clickedTargetElement.classList.remove(
						'lg:after:absolute',
						'lg:after:bottom-0',
						'lg:after:left-0',
						'lg:after:w-full',
						'lg:after:h-[100px]',
						'lg:after:bg-gradient-to-b',
						'lg:after:from-transparent',
						'lg:after:to-white',
						'lg:after:z-[500]',
						'lg:after:rounded-b-lg'
					);
				} else {
					// Collapse - set appropriate height based on content type
					if (isCompanyDescription) {
						clickedTargetElement.style.maxHeight = '200px';
					} else {
						clickedTargetElement.style.maxHeight = '320px';
					}
					clickedTargetElement.style.overflow = 'hidden';

					// Add gradient classes back when collapsed
					clickedTargetElement.classList.add(
						'lg:after:absolute',
						'lg:after:bottom-0',
						'lg:after:left-0',
						'lg:after:w-full',
						'lg:after:h-[100px]',
						'lg:after:bg-gradient-to-b',
						'lg:after:from-transparent',
						'lg:after:to-white',
						'lg:after:z-[500]',
						'lg:after:rounded-b-lg'
					);
				}
			}

			if (parentElement) {
				parentElement.classList.toggle('parent-is-active');
			}

			// Toggle text content
			const readMoreText = this.querySelector('.readmore');
			const showLessTextElement = this.querySelector('.showless');

			if (readMoreText && showLessTextElement) {
				if (
					clickedTargetElement &&
					clickedTargetElement.classList.contains('is-active')
				) {
					readMoreText.style.display = 'none';
					showLessTextElement.style.display = 'inline';
				} else {
					readMoreText.style.display = 'inline';
					showLessTextElement.style.display = 'none';
				}
			}

			// Toggle icon rotation
			const icon = this.querySelector('svg');
			if (icon) {
				icon.classList.toggle('-rotate-180');
				icon.classList.toggle('transform');
				icon.classList.toggle('transition-transform');
				icon.classList.toggle('duration-300');
			}

			// Restore the scroll position after DOM updates
			setTimeout(() => {
				window.scrollTo({
					top: scrollPosition,
					behavior: 'instant', // Use instant to avoid smooth scrolling animation
				});
			}, 0);
		});
	});
}

// Initialize sticky header shadow on scroll
function initStickyHeaderShadow() {
	const stickyHeader = document.querySelector('header.lg\\:sticky');
	if (!stickyHeader) return;

	const handleScroll = () => {
		if (window.scrollY > 10) {
			stickyHeader.classList.add('shadow-md');
		} else {
			stickyHeader.classList.remove('shadow-md');
		}
	};

	// Add scroll event listener
	window.addEventListener('scroll', handleScroll, { passive: true });

	// Initial check in case page is loaded scrolled down
	handleScroll();
}

// Consolidated DOMContentLoaded event handler
document.addEventListener('DOMContentLoaded', () => {
	// Critical: Initialize sticky header immediately (above the fold)
	initStickyHeaderShadow();

	// Critical: Initialize show more toggles (likely visible)
	initShowMoreToggles();

	// Load Preline immediately if needed (interactive elements need to work right away)
	loadPreline();

	// Non-critical: Load SVG sprite with lower priority
	requestIdleCallback(() => {
		loadSVGSprite();
	}, { timeout: 1000 });

	// Lazy load sliders and galleries (will load when near viewport)
	initSliders();
	initGalleries();
});
